import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";
import type { ServerError } from "@/lib/api/errors/ErrorHandler";
import type { TableFilter } from "@/types/TableFilter";
import type { TableView } from "@/types/TableView";
import {
  ROLE_DEFAULT_SURFACES,
  type RoleDefaultSurface,
  type RoleSurfaceDefault,
} from "@/types/RoleSurfaceDefault";
import {
  fetchFiltersList,
  fetchViewsList,
} from "@/lib/api/handlers/ViewFilterHandling";
import {
  FILTERS_FIRST_PAGE,
  VIEWS_FIRST_PAGE,
} from "@/lib/template-transfer/tableViews/constants";
import { CollapsibleSection } from "@/components/CollapsibleSection/CollapsibleSection";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";

/** Option value for "no default" — OptionInput works on strings, and an empty
 *  string is also what its blur commits when the box is cleared, so both are
 *  read as "none" on the way out. */
const NONE = "";

/**
 * i18n key naming each list. Exported so the i18n guard asserts over the REAL
 * map — these three keys were missing from all 57 locales for weeks and the
 * section rendered their dotted paths, because nothing compares the keys a
 * component asks for against the messages that exist.
 */
export const SURFACE_LABEL_KEY: Record<RoleDefaultSurface, string> = {
  guest: "surfaceGuest",
  registrant: "surfaceRegistrant",
  companion: "surfaceCompanion",
};

function entryOf(
  value: RoleSurfaceDefault[],
  surface: RoleDefaultSurface,
): RoleSurfaceDefault | undefined {
  return value.find((d) => d.surface === surface);
}

/**
 * One list's row: a view picker and a filter picker.
 *
 * Its own component so each surface owns its two queries — a `useQuery` inside
 * a `.map()` in the parent would change hook order the moment the surface list
 * ever became dynamic.
 */
function SurfaceDefaultRow({
  eventId,
  surface,
  entry,
  onChange,
  disabled,
}: {
  eventId: string;
  surface: RoleDefaultSurface;
  entry: RoleSurfaceDefault | undefined;
  onChange: (next: RoleSurfaceDefault) => void;
  disabled: boolean;
}) {
  const t = useTranslations("roleTemplates");
  const tErr = useTranslations("errors");

  // Both lists are EVENT-shared, so these are the very rows the collaborator
  // will see. Only fetched while the section is open — CollapsibleSection
  // unmounts its children when collapsed.
  const viewsQ = useQuery<TableView[] | ServerError>({
    queryKey: ["roleSurfaceDefaults", "views", surface, eventId],
    // Page is ONE-indexed on the views endpoint (TableViewService rejects
    // page < 1 with a 400) but ZERO-indexed on the filters endpoint
    // (TableFilterRepository skips `page * size`) — an asymmetry that cost this
    // section first its entire view list (a 0 here: every picker offered "No
    // default" and nothing else, from the day it shipped) and then, once the
    // views line read 1, its filter list on every event with fewer than 101
    // saved filters (the same 1 on the filters line asked for rows 101-200).
    // Both bases are the pinned constants now (PB-RTI-1).
    queryFn: () => fetchViewsList([], surface, eventId, VIEWS_FIRST_PAGE, 100),
    enabled: !!eventId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  const filtersQ = useQuery<TableFilter[] | ServerError>({
    queryKey: ["roleSurfaceDefaults", "filters", surface, eventId],
    queryFn: () => fetchFiltersList([], surface, eventId, FILTERS_FIRST_PAGE, 100),
    enabled: !!eventId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const views = useMemo(
    () => (Array.isArray(viewsQ.data) ? viewsQ.data : []),
    [viewsQ.data],
  );
  const filters = useMemo(
    () => (Array.isArray(filtersQ.data) ? filtersQ.data : []),
    [filtersQ.data],
  );

  // `fetchViewsList`/`fetchFiltersList` do not reject — they catch and return a
  // ServerError object, so `useQuery` never enters isError and a 5xx arrives
  // here indistinguishable from "this event has no saved views". Left
  // unhandled, an outage renders every stored default as "Deleted" and offers
  // only "No default"; the natural repair is to clear the row, and THAT write
  // destroys a perfectly good default. So a failed load disables the picker
  // instead of inviting a save.
  const viewsFailed = !viewsQ.isLoading && !Array.isArray(viewsQ.data);
  const filtersFailed = !filtersQ.isLoading && !Array.isArray(filtersQ.data);

  const viewOptions = useMemo(
    () => [NONE, ...views.map((v) => v.tableViewId ?? "").filter(Boolean)],
    [views],
  );
  const filterOptions = useMemo(
    () => [NONE, ...filters.map((f) => f.tableFilterId ?? "").filter(Boolean)],
    [filters],
  );
  const viewLabels = useMemo(() => {
    const m: Record<string, string> = { [NONE]: t("surfaceDefaultNone") };
    for (const v of views) if (v.tableViewId) m[v.tableViewId] = v.name;
    // A stored id whose view was deleted since must still render as SOMETHING,
    // or the box shows a raw ObjectId and reads like corruption. Only claim
    // "Deleted" when the list actually loaded — after a failed fetch every id
    // is missing, and calling them all deleted is a lie about the data.
    if (entry?.tableViewId && !m[entry.tableViewId] && !viewsFailed) {
      m[entry.tableViewId] = t("surfaceDefaultMissing");
    }
    return m;
  }, [views, entry?.tableViewId, t, viewsFailed]);
  const filterLabels = useMemo(() => {
    const m: Record<string, string> = { [NONE]: t("surfaceDefaultNone") };
    for (const f of filters) if (f.tableFilterId) m[f.tableFilterId] = f.name;
    if (entry?.tableFilterId && !m[entry.tableFilterId] && !filtersFailed) {
      m[entry.tableFilterId] = t("surfaceDefaultMissing");
    }
    return m;
  }, [filters, entry?.tableFilterId, t, filtersFailed]);

  const loading = viewsQ.isLoading || filtersQ.isLoading;

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`surface-default-row-${surface}`}
    >
      <div className="flex items-center gap-2">
        <span className="body-3-medium">{t(SURFACE_LABEL_KEY[surface])}</span>
        {loading && (
          // The bare spinner, not InlineLoadingSpinner: that wrapper carries
          // `min-h-[120px]` for page-level use, and three of them in a modal
          // header row add ~360px that collapses when the queries land —
          // moving Save out from under the cursor.
          <span data-testid={`surface-default-loading-${surface}`}>
            <LoadingSpinner />
          </span>
        )}
        {!loading && !viewsFailed && views.length === 0 && (
          // The pickers only ever offer "No default" on an event with nothing
          // saved, which reads as a broken control rather than an empty
          // catalogue. `nothingFoundText` cannot say this: the NONE entry means
          // the option list is never empty, so OptionInput's own empty state is
          // unreachable here.
          <span
            className="body-4-regular font-tertiary"
            data-testid={`surface-default-empty-${surface}`}
          >
            {t("noSavedViews")}
          </span>
        )}
        {(viewsFailed || filtersFailed) && (
          <span
            className="body-4-regular font-error"
            data-testid={`surface-default-error-${surface}`}
          >
            {tErr("serverError")}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <OptionInput
          required={false}
          testId={`surface-default-view-${surface}`}
          label={t("surfaceDefaultView")}
          // Six comboboxes in one modal whose only visible names are "View" and
          // "Filter", three times over: the list they belong to is a sibling
          // span, which no assistive technology associates with the input.
          ariaLabel={`${t(SURFACE_LABEL_KEY[surface])} – ${t("surfaceDefaultView")}`}
          options={viewOptions}
          optionLabels={viewLabels}
          // A dangling id is kept as the value on purpose: silently dropping it
          // would rewrite the template the first time anyone opened the editor.
          defaultValue={entry?.tableViewId ?? NONE}
          disabled={disabled || viewsFailed}
          strictOption
          nothingFoundText={t("noSavedViews")}
          setParent={(v) =>
            onChange({ surface, tableViewId: v || undefined, tableFilterId: entry?.tableFilterId })
          }
        />
        <OptionInput
          required={false}
          testId={`surface-default-filter-${surface}`}
          label={t("surfaceDefaultFilter")}
          ariaLabel={`${t(SURFACE_LABEL_KEY[surface])} – ${t("surfaceDefaultFilter")}`}
          options={filterOptions}
          optionLabels={filterLabels}
          defaultValue={entry?.tableFilterId ?? NONE}
          disabled={disabled || filtersFailed}
          strictOption
          nothingFoundText={t("noSavedFilters")}
          setParent={(v) =>
            onChange({ surface, tableViewId: entry?.tableViewId, tableFilterId: v || undefined })
          }
        />
      </div>
    </div>
  );
}

/**
 * Optional editor block: which saved view and filter a list starts on.
 *
 * Two owners share it — a ROLE TEMPLATE (everyone carrying the role) and a
 * single COLLABORATOR row (the invite / edit-collaborator modal, which wins
 * over the template for the same surface). Only the wording differs, hence
 * `hint` and `testId`; the pickers, the queries and the "drop rows pointing at
 * nothing" rule are identical on purpose.
 *
 * A default and nothing more — the cockpit applies it where the user has no
 * preference of their own, and they can switch away at any time. The enforcing
 * knobs are the row filters and the field allowlist; this one is convenience,
 * which is exactly why it lives behind a disclosure.
 */
export function SurfaceDefaultsSection({
  eventId,
  value,
  onChange,
  disabled = false,
  hint,
  testId = "role-template-surface-defaults",
}: {
  eventId: string;
  value: RoleSurfaceDefault[];
  onChange: (next: RoleSurfaceDefault[]) => void;
  disabled?: boolean;
  /** Overrides the role-template wording of the ⓘ note. */
  hint?: string;
  testId?: string;
}) {
  const t = useTranslations("roleTemplates");

  // Rows that actually point at something — the summary must not count a row
  // the admin opened and left on "no default".
  const configured = value.filter(
    (d) => d.tableViewId || d.tableFilterId,
  ).length;

  const update = (next: RoleSurfaceDefault) => {
    const rest = value.filter((d) => d.surface !== next.surface);
    // Rows pointing at nothing are dropped here as well as server-side, so the
    // dirty check does not fire for opening and closing a dropdown.
    if (!next.tableViewId && !next.tableFilterId) {
      onChange(rest);
      return;
    }
    onChange([...rest, next]);
  };

  return (
    <CollapsibleSection
      title={t("surfaceDefaults")}
      optionalLabel={t("surfaceDefaultsOptional")}
      infoMessage={hint ?? t("surfaceDefaultsHint")}
      defaultOpen={configured > 0}
      summary={
        configured > 0 ? (
          <span className="body-4-regular font-tertiary">
            {t("surfaceDefaultsConfigured", { count: configured })}
          </span>
        ) : undefined
      }
      testId={testId}
    >
      <div className="flex flex-col gap-4">
        {ROLE_DEFAULT_SURFACES.map((surface) => (
          <SurfaceDefaultRow
            key={surface}
            eventId={eventId}
            surface={surface}
            entry={entryOf(value, surface)}
            onChange={update}
            disabled={disabled}
          />
        ))}
      </div>
    </CollapsibleSection>
  );
}
